// src/pages/Session.js
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import {
  Pause, Play, Square, Plus, Trash2, ArrowLeft, Clock,
  IndianRupee, Utensils, AlertTriangle, Users, UserPlus
} from 'lucide-react';
import { Modal, CustomerSearch } from './Tables';

const MAX_SECONDS = 6 * 3600;
const BLOCK_SECS  = 600;       // 10-min minimum
const POLL_MS     = 30000;

// Play a bell/ring sound using Web Audio API — no external file needed
function ringBell() {
  try {
    const ctx  = new (window.AudioContext || window.webkitAudioContext)();
    const play = (freq, startTime, duration, gain = 0.6) => {
      const osc = ctx.createOscillator();
      const vol = ctx.createGain();
      osc.connect(vol);
      vol.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, startTime);
      vol.gain.setValueAtTime(gain, startTime);
      vol.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
      osc.start(startTime);
      osc.stop(startTime + duration);
    };
    const t = ctx.currentTime;
    // Three-ring bell: high ding, pause, high ding, pause, high ding
    play(880, t,       0.8);
    play(660, t,       0.8, 0.3);
    play(880, t + 1.0, 0.8);
    play(660, t + 1.0, 0.8, 0.3);
    play(880, t + 2.0, 1.2);
    play(660, t + 2.0, 1.2, 0.3);
  } catch (_) {}
}

// Billing logic:
// - First 10 min (0-600s): charge flat 10 min regardless of actual time
// - After 10 min (600s+): charge actual seconds elapsed (continuous, per second)
function billedSecs(actual) {
  if (actual <= 0) return 0;
  if (actual <= BLOCK_SECS) return BLOCK_SECS;  // min 10 min flat
  return actual;                                  // after 10 min = actual seconds
}
function calcPlayAmt(actual, rate) {
  return (billedSecs(actual) / 3600) * rate;
}
// Block progress bar — only shows during the first 10-min block
function secsIntoBlock(actual) {
  if (actual <= 0) return 0;
  if (actual >= BLOCK_SECS) return BLOCK_SECS;  // block complete
  return actual;
}

export default function Session() {
  const { id }   = useParams();
  const navigate = useNavigate();

  const [session,        setSession]        = useState(null);
  const [menu,           setMenu]           = useState([]);
  const [elapsed,        setElapsed]        = useState(0);
  const [orderModal,     setOrderModal]     = useState(false);
  const [endModal,       setEndModal]       = useState(false);
  const [limitModal,     setLimitModal]     = useState(false);
  const [addPlayerModal, setAddPlayerModal] = useState(false);
  const [orderForm,      setOrderForm]      = useState({ menu_item_id:'', quantity:1, unit_price:'', customer_id:'' });
  const [splitMode,      setSplitMode]      = useState('equal');  // 'equal'|'loser'|'custom'
  const [loser_id,       setLoserId]        = useState(null);
  const [customPayers,   setCustomPayers]   = useState([]);           // array of cids sharing table
  const [ending,         setEnding]         = useState(false);

  const timerRef   = useRef(null);
  const pollRef    = useRef(null);
  const warnRef    = useRef(false);
  const blockRung  = useRef(false);  // ring played for this 10-min block
  const elapsedRef = useRef(0);

  const calcElapsed = useCallback((sess) => {
    if (!sess) return 0;
    let base = parseInt(sess.total_play_seconds || 0);
    if (sess.status === 'active') {
      const open = (sess.intervals || []).find(i => !i.ended_ts);
      if (open?.started_ts) {
        base += Math.max(0, Math.floor(Date.now()/1000) - parseInt(open.started_ts));
      }
    }
    return Math.max(0, base);
  }, []);

  const load = useCallback(async (silent = false) => {
    try {
      const [s, m] = await Promise.all([
        api.get('/sessions?id=' + id),
        api.get('/food?type=menu'),
      ]);
      setSession(s.data);
      setMenu(Array.isArray(m.data) ? m.data : []);
      const secs = calcElapsed(s.data);
      setElapsed(secs);
      elapsedRef.current = secs;
    } catch { if (!silent) toast.error('Failed to load session'); }
  }, [id, calcElapsed]);

  useEffect(() => { load(); }, [load]);

  // Live timer
  useEffect(() => {
    clearInterval(timerRef.current);
    if (session?.status === 'active') {
      timerRef.current = setInterval(() => {
        setElapsed(e => {
          const n = e + 1;
          elapsedRef.current = n;
          if (n >= MAX_SECONDS && !warnRef.current) { warnRef.current = true; setLimitModal(true); }
          // Ring bell exactly when 10-min minimum block completes
          if (n === BLOCK_SECS && !blockRung.current) { blockRung.current = true; ringBell(); }
          return n;
        });
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [session?.status]);

  useEffect(() => {
    pollRef.current = setInterval(() => load(true), POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [load]);

  // ── Actions ───────────────────────────────────────────────
  const pause = async () => {
    try { await api.post('/sessions?action=pause', { session_id: id }); toast.success('Paused'); load(); }
    catch (e) { toast.error(e.response?.data?.error || 'Error'); }
  };

  const resume = async () => {
    warnRef.current = false;
    blockRung.current = false;
    try { await api.post('/sessions?action=resume', { session_id: id }); toast.success('Resumed'); load(); }
    catch (e) { toast.error(e.response?.data?.error || 'Error'); }
  };

  const endSession = async () => {
    setEnding(true);
    try {
      await api.post('/sessions?action=end', {
        session_id:        id,
        split_mode:        splitMode,
        loser_customer_id: loser_id || 0,
        custom_payer_ids:  customPayers,
      });
      toast.success('Session ended — charges moved to Billing');
      setEndModal(false);
      navigate('/tables');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Error');
    } finally { setEnding(false); }
  };

  const addPlayerToSession = async (customer) => {
    try {
      await api.post('/sessions?action=add-player', { session_id: id, customer_id: customer.id });
      toast.success(`${customer.name} added`);
      setAddPlayerModal(false);
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Error'); }
  };

  // ── Food ─────────────────────────────────────────────────
  const addOrder = async () => {
    const item = menu.find(m => m.id === parseInt(orderForm.menu_item_id));
    if (!item)               return toast.error('Select a menu item');
    if (item.is_out_of_stock)return toast.error(`${item.name} is out of stock`);
    if (!orderForm.unit_price) return toast.error('Enter price');
    const qty = parseInt(orderForm.quantity) || 1;
    if (item.max_orderable < qty) return toast.error(`Only ${item.max_orderable} available`);
    try {
      await api.post('/food?type=order', {
        session_id:   id,
        menu_item_id: item.id,
        item_name:    item.name,
        quantity:     qty,
        unit_price:   orderForm.unit_price,
        customer_id:  orderForm.customer_id || null,
      });
      toast.success('Order added');
      setOrderModal(false);
      load();
    } catch (e) {
      const err = e.response?.data;
      toast.error(err?.out_of_stock ? `Out of stock: ${err.detail}` : err?.error || 'Error');
    }
  };

  const removeOrder = async (oid) => {
    if (!window.confirm('Remove this order?')) return;
    try { await api.delete('/food?type=order&id=' + oid); toast.success('Removed'); load(); }
    catch (e) { toast.error(e.response?.data?.error || 'Error'); }
  };

  const onMenuSelect = (e) => {
    const item = menu.find(m => m.id === parseInt(e.target.value));
    setOrderForm(f => ({ ...f, menu_item_id: e.target.value, unit_price: item?.selling_price || '' }));
  };

  // ── Helpers ───────────────────────────────────────────────
  const fmtTime = s => {
    const t = Math.max(0, Math.floor(s));
    return `${String(Math.floor(t/3600)).padStart(2,'0')}:${String(Math.floor((t%3600)/60)).padStart(2,'0')}:${String(t%60).padStart(2,'0')}`;
  };

  const rate        = parseFloat(session?.hourly_rate || 0);
  const playAmt     = calcPlayAmt(elapsed, rate);
  const pct6h       = Math.min(100, Math.round((elapsed / MAX_SECONDS) * 100));

  // Block progress
  const minBlockDone = elapsed >= BLOCK_SECS;
  const inBlock      = secsIntoBlock(elapsed);
  const blockPct     = Math.round((inBlock / BLOCK_SECS) * 100);
  const secsLeft     = Math.max(0, BLOCK_SECS - elapsed);

  if (!session) return <div className="loading-screen"><div className="spinner"/></div>;

  const isEnded    = session.status === 'ended';
  const players    = session.players || [];
  const foodOrders = session.food_orders || [];
  const foodTotal  = foodOrders.reduce((a, o) => a + parseFloat(o.total_price), 0);
  const grandTotal = isEnded ? parseFloat(session.total_amount) : playAmt + foodTotal;

  return (
    <div>
      <div className="page-header">
        <button className="btn btn-outline btn-sm" onClick={() => navigate('/tables')}>
          <ArrowLeft size={15}/> Back
        </button>
        <h2 className="page-title">{session.table_name}</h2>
        <span className={`badge badge-${session.status}`}>{session.status}</span>
      </div>

      {/* Players row */}
      <div className="session-players-row">
        <Users size={16} color="#64748b"/>
        {players.map(p => (
          <div key={p.customer_id} className="session-player-chip">
            <span>{p.customer_name}</span>
            <span className="chip-mobile">{p.mobile}</span>
          </div>
        ))}
        {!isEnded && (
          <button className="btn btn-outline btn-sm" style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:5 }}
            onClick={() => setAddPlayerModal(true)}>
            <UserPlus size={14}/> Add Player
          </button>
        )}
      </div>

      {/* Timer hero */}
      <div className="session-hero">
        <div className="session-timer">
          <Clock size={28} className="timer-icon"/>
          <span className="timer-display">{fmtTime(elapsed)}</span>
        </div>

        {/* 10-min block progress */}
        {!isEnded && elapsed >= 0 && (
          <div className="block-billing-info">
            <div className="block-billing-header">
              {!minBlockDone ? (
                <>
                  <span>⏱ Minimum 10-min block</span>
                  <span style={{ color: secsLeft <= 60 ? '#ef4444' : secsLeft <= 120 ? '#f97316' : '#0369a1' }}>
                    {fmtTime(secsLeft)} remaining
                  </span>
                </>
              ) : (
                <>
                  <span style={{ color:'#15803d' }}>✓ Min. period done — billing per second</span>
                  <span style={{ color:'#15803d', fontWeight:700 }}>₹{playAmt.toFixed(2)}</span>
                </>
              )}
            </div>
            <div className="block-progress-track">
              <div className="block-progress-fill" style={{
                width: blockPct + '%',
                background: minBlockDone ? '#22c55e' : blockPct >= 90 ? '#f97316' : '#22c55e'
              }}/>
            </div>
            <div style={{ fontSize:11, color:'#94a3b8', marginTop:3 }}>
              {minBlockDone
                ? `Actual time: ${fmtTime(elapsed)} · Rate ₹${rate}/hr`
                : `Billed: ${fmtTime(billedSecs(elapsed))} · Rate ₹${rate}/hr`}
            </div>
          </div>
        )}

        {/* 6h limit bar */}
        {!isEnded && (
          <div className="session-limit-bar">
            <div className="session-limit-track">
              <div className="session-limit-fill" style={{ width:pct6h+'%', background:pct6h>=100?'#ef4444':pct6h>=80?'#f97316':'#22c55e' }}/>
            </div>
            <span className="session-limit-label" style={{ color:pct6h>=100?'#ef4444':'#64748b' }}>
              {pct6h>=100 ? '⚠ 6h limit reached' : `${fmtTime(MAX_SECONDS-elapsed)} of 6h remaining`}
            </span>
          </div>
        )}

        <div className="session-amounts">
          <div className="amount-item">
            <span>Play (billed)</span>
            <strong>₹{isEnded ? Number(session.play_amount).toFixed(2) : playAmt.toFixed(2)}</strong>
          </div>
          <div className="amount-item">
            <span>Food</span>
            <strong>₹{foodTotal.toFixed(2)}</strong>
          </div>
          <div className="amount-item total-item">
            <span>Total</span>
            <strong>₹{grandTotal.toFixed(2)}</strong>
          </div>
        </div>

        {!isEnded && (
          <div className="session-controls">
            {session.status === 'active'
              ? <button className="btn btn-warning" onClick={pause}><Pause size={16}/> Pause</button>
              : <button className="btn btn-success" onClick={resume}><Play size={16}/> Resume</button>
            }
            <button className="btn btn-primary" onClick={() => setOrderModal(true)}><Plus size={16}/> Add Food</button>
            <button className="btn btn-danger"  onClick={() => setEndModal(true)}><Square size={16}/> End Session</button>
          </div>
        )}
        {isEnded && (
          <div className="paid-badge">
            ✅ Session Ended — charges sent to Billing module
          </div>
        )}
      </div>

      {/* Food orders */}
      <div className="card">
        <div className="card-header"><Utensils size={18}/><span>Food Orders</span></div>
        {foodOrders.length === 0 ? <p className="empty-text">No food orders yet</p> : (
          <table className="data-table">
            <thead><tr><th>Item</th><th>Ordered By</th><th>Qty</th><th>Price</th><th>Total</th>{!isEnded && <th></th>}</tr></thead>
            <tbody>
              {foodOrders.map(o => (
                <tr key={o.id}>
                  <td>{o.item_name}</td>
                  <td><span style={{ fontSize:12, color:'#64748b', background:'#f1f5f9', padding:'2px 8px', borderRadius:12 }}>{o.customer_name || '—'}</span></td>
                  <td>{o.quantity}</td>
                  <td>₹{Number(o.unit_price).toFixed(2)}</td>
                  <td>₹{Number(o.total_price).toFixed(2)}</td>
                  {!isEnded && <td><button className="btn btn-danger btn-sm" onClick={() => removeOrder(o.id)}><Trash2 size={13}/></button></td>}
                </tr>
              ))}
            </tbody>
            <tfoot><tr><td colSpan={4}><strong>Food Total</strong></td><td colSpan={2}><strong>₹{foodTotal.toFixed(2)}</strong></td></tr></tfoot>
          </table>
        )}
      </div>

      {/* Add Food Modal */}
      {orderModal && (
        <Modal title="Add Food Order" onClose={() => setOrderModal(false)}>
          <div className="form-group">
            <label>Ordered By</label>
            <select value={orderForm.customer_id} onChange={e => setOrderForm(f => ({...f, customer_id: e.target.value}))}>
              <option value="">— Select customer —</option>
              {players.map(p => <option key={p.customer_id} value={p.customer_id}>{p.customer_name} ({p.mobile})</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Menu Item</label>
            <select value={orderForm.menu_item_id} onChange={onMenuSelect}>
              <option value="">Select item…</option>
              {menu.map(m => (
                <option key={m.id} value={m.id} disabled={m.is_out_of_stock} style={{ color:m.is_out_of_stock?'#ef4444':'' }}>
                  {m.name} ({m.price_type}){m.is_out_of_stock?' — OUT OF STOCK':m.max_orderable<10&&m.max_orderable<9999?` — ${m.max_orderable} left`:''}
                </option>
              ))}
            </select>
            {orderForm.menu_item_id && (() => {
              const item = menu.find(m => m.id === parseInt(orderForm.menu_item_id));
              if (!item) return null;
              if (item.is_out_of_stock) return <div className="stock-order-warning stock-order-out">🚫 Out of stock</div>;
              if (item.max_orderable < 9999) return <div className="stock-order-warning stock-order-low">⚠️ Only {item.max_orderable} available</div>;
              return null;
            })()}
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Quantity</label>
              <input type="number" min="1" value={orderForm.quantity} onChange={e => setOrderForm(f => ({...f,quantity:e.target.value}))}/>
            </div>
            <div className="form-group">
              <label>Unit Price (₹)</label>
              <input type="number" step="0.01" value={orderForm.unit_price} onChange={e => setOrderForm(f => ({...f,unit_price:e.target.value}))}/>
            </div>
          </div>
          {orderForm.unit_price && orderForm.quantity && (
            <div style={{ background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:8, padding:'8px 14px', fontSize:13, color:'#15803d' }}>
              Total: ₹{(parseFloat(orderForm.unit_price||0)*parseInt(orderForm.quantity||1)).toFixed(2)}
            </div>
          )}
          <div className="modal-actions">
            <button className="btn btn-outline" onClick={() => setOrderModal(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={addOrder} disabled={!!menu.find(m=>m.id===parseInt(orderForm.menu_item_id))?.is_out_of_stock}>
              <Plus size={14}/> Add Order
            </button>
          </div>
        </Modal>
      )}

      {/* Add Player Modal */}
      {addPlayerModal && (
        <Modal title="Add Player to Session" onClose={() => setAddPlayerModal(false)}>
          <p style={{ fontSize:13, color:'#64748b', marginBottom:12 }}>
            Search and add a customer who wants to join this session.
          </p>
          <div className="form-group">
            <label>Search Customer</label>
            <CustomerSearch
              excludeIds={players.map(p => p.customer_id)}
              onSelect={addPlayerToSession}
              placeholder="Type name or mobile…"
            />
          </div>
          <div style={{ background:'#fef9c3', border:'1px solid #fde68a', borderRadius:8, padding:'10px 14px', fontSize:13, color:'#92400e', marginTop:8 }}>
            ⚠️ New players share the table bill equally from this point.
          </div>
        </Modal>
      )}

      {/* End Session Modal — simple, no payment */}
      {endModal && (
        <Modal title="End Session" onClose={() => setEndModal(false)}>
          {/* Billing summary */}
          <div className="bill-time-summary">
            <div className="bill-time-row">
              <span>Actual play time</span><span>{fmtTime(elapsed)}</span>
            </div>
            <div className="bill-time-row">
              <span>Billed time (10-min blocks)</span>
              <span style={{ color:'#2563eb', fontWeight:700 }}>{fmtTime(billedSecs(elapsed))}</span>
            </div>
            <div className="bill-time-row">
              <span>Rate</span><span>₹{rate}/hr</span>
            </div>
            <div className="bill-time-row total">
              <span>Table Charges</span><span>₹{playAmt.toFixed(2)}</span>
            </div>
            <div className="bill-time-row total">
              <span>Food Charges</span><span>₹{foodTotal.toFixed(2)}</span>
            </div>
            <div className="bill-time-row total" style={{ fontSize:16, borderTop:'2px solid #e2e8f0', marginTop:4, paddingTop:8 }}>
              <span>Total (all players)</span><span style={{ color:'#1e3a5f' }}>₹{(playAmt + foodTotal).toFixed(2)}</span>
            </div>
          </div>

          {/* Table payment split */}
          {players.length > 1 && (
            <div style={{ margin:'14px 0 4px' }}>
              <div style={{ fontSize:13, color:'#64748b', marginBottom:8, fontWeight:600 }}>
                How should table charges be split?
              </div>
              {/* Mode selector */}
              <div style={{ display:'flex', gap:8, marginBottom:10, flexWrap:'wrap' }}>
                <button
                  className={`btn btn-sm ${splitMode==='equal'?'btn-primary':'btn-outline'}`}
                  onClick={() => { setSplitMode('equal'); setLoserId(null); setCustomPayers([]); }}>
                  Split Equally
                </button>
                <button
                  className={`btn btn-sm ${splitMode==='loser'?'btn-danger':'btn-outline'}`}
                  onClick={() => { setSplitMode('loser'); setCustomPayers([]); }}>
                  One Person Pays All
                </button>
                <button
                  className={`btn btn-sm ${splitMode==='custom'?'btn-warning':'btn-outline'}`}
                  onClick={() => { setSplitMode('custom'); setLoserId(null); setCustomPayers([]); }}>
                  Select Who Pays
                </button>
              </div>

              {/* Loser mode — pick one */}
              {splitMode === 'loser' && (
                <div>
                  <div style={{ fontSize:12, color:'#64748b', marginBottom:6 }}>Select the player who pays the full table bill:</div>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                    {players.map(p => (
                      <button key={p.customer_id}
                        className={`btn btn-sm ${loser_id===p.customer_id?'btn-danger':'btn-outline'}`}
                        onClick={() => setLoserId(p.customer_id)}>
                        {p.customer_name}
                        {loser_id===p.customer_id && ' ✓'}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Custom mode — pick multiple */}
              {splitMode === 'custom' && (
                <div>
                  <div style={{ fontSize:12, color:'#64748b', marginBottom:6 }}>
                    Select which players share the table bill (others pay ₹0 for table):
                  </div>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                    {players.map(p => {
                      const selected = customPayers.includes(p.customer_id);
                      return (
                        <button key={p.customer_id}
                          className={`btn btn-sm ${selected?'btn-primary':'btn-outline'}`}
                          onClick={() => setCustomPayers(prev =>
                            selected ? prev.filter(id => id !== p.customer_id) : [...prev, p.customer_id]
                          )}>
                          {p.customer_name}
                          {selected && ' ✓'}
                        </button>
                      );
                    })}
                  </div>
                  {customPayers.length > 0 && (
                    <div style={{ fontSize:12, color:'#2563eb', marginTop:6 }}>
                      ₹{playAmt.toFixed(2)} split among {customPayers.length} player{customPayers.length!==1?'s':''}
                      = ₹{(playAmt / customPayers.length).toFixed(2)} each
                    </div>
                  )}
                  {customPayers.length === 0 && (
                    <div style={{ fontSize:12, color:'#f97316', marginTop:6 }}>
                      ⚠ Select at least one player
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Per-player summary — amounts only */}
          <div style={{ border:'1px solid #e2e8f0', borderRadius:10, overflow:'hidden', margin:'14px 0 4px' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead>
                <tr style={{ background:'#1e3a5f', color:'#fff' }}>
                  <th style={{ padding:'8px 12px', textAlign:'left' }}>Customer</th>
                  <th style={{ padding:'8px 12px', textAlign:'right' }}>Table</th>
                  <th style={{ padding:'8px 12px', textAlign:'right' }}>Food</th>
                  <th style={{ padding:'8px 12px', textAlign:'right' }}>Session Total</th>
                </tr>
              </thead>
              <tbody>
                {players.map((p, i) => {
                  const foodMap = {};
                  foodOrders.forEach(o => { foodMap[o.customer_id] = (foodMap[o.customer_id]||0) + parseFloat(o.total_price); });
                  let pa = 0;
                  if (splitMode === 'equal') {
                    pa = Math.round((playAmt / players.length) * 100) / 100;
                  } else if (splitMode === 'loser') {
                    pa = p.customer_id === loser_id ? playAmt : 0;
                  } else if (splitMode === 'custom') {
                    const n = customPayers.length || 1;
                    pa = customPayers.includes(p.customer_id) ? Math.round((playAmt / n) * 100) / 100 : 0;
                  }
                  const fa = foodMap[p.customer_id] || 0;
                  return (
                    <tr key={p.customer_id} style={{ background:i%2?'#f8fafc':'#fff' }}>
                      <td style={{ padding:'8px 12px' }}>
                        <div style={{ fontWeight:600 }}>{p.customer_name}</div>
                        <div style={{ fontSize:11, color:'#94a3b8' }}>{p.mobile}</div>
                      </td>
                      <td style={{ padding:'8px 12px', textAlign:'right' }}>₹{pa.toFixed(2)}</td>
                      <td style={{ padding:'8px 12px', textAlign:'right' }}>₹{fa.toFixed(2)}</td>
                      <td style={{ padding:'8px 12px', textAlign:'right', fontWeight:700, color:'#1e3a5f' }}>₹{(pa+fa).toFixed(2)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:8, padding:'10px 14px', fontSize:13, color:'#1d4ed8', marginBottom:8 }}>
            💡 Payment & discounts are handled in the <strong>Billing</strong> module after ending the session.
          </div>

          <div className="modal-actions">
            <button className="btn btn-outline" onClick={() => setEndModal(false)}>Cancel</button>
            <button className="btn btn-danger" onClick={endSession} disabled={ending}>
              <Square size={14}/> {ending ? 'Ending…' : 'Confirm End Session'}
            </button>
          </div>
        </Modal>
      )}

      {/* 6h warning */}
      {limitModal && (
        <Modal title="⚠️ 6-Hour Limit Reached" onClose={() => setLimitModal(false)}>
          <div style={{ textAlign:'center', padding:'8px 0 16px' }}>
            <AlertTriangle size={48} color="#f97316" style={{ margin:'0 auto 12px' }}/>
            <p style={{ fontSize:15, color:'#334155', lineHeight:1.6 }}>
              Session has been running <strong>6 hours</strong>.<br/>Continue or end now?
            </p>
            <div style={{ background:'#fef9c3', border:'1px solid #fde68a', borderRadius:8, padding:'10px 16px', margin:'12px 0', fontSize:13, color:'#92400e' }}>
              Current bill: <strong>₹{grandTotal.toFixed(2)}</strong>
            </div>
          </div>
          <div className="modal-actions" style={{ justifyContent:'center', gap:12 }}>
            <button className="btn btn-success" onClick={() => { setLimitModal(false); warnRef.current=false; }}>
              <Play size={14}/> Continue Playing
            </button>
            <button className="btn btn-danger" onClick={() => { setLimitModal(false); setEndModal(true); }}>
              <Square size={14}/> End Session
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
